import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Address3HelperService, AddressFormGroup } from '../address3-helper.service';
import { CustomValidators, regexValidators } from '@shared/customValidators';
import { first } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'address3-uk',
  templateUrl: './address3-uk.component.html'
})
export class Address3UkComponent implements OnInit {

    @Input()
    readonly = false;

    @Input()
    addressFormGroup: FormGroup<AddressFormGroup>;

    constructor(
        private readonly logger: NGXLogger,
        private readonly destroyRef: DestroyRef,
        private readonly address3Helper: Address3HelperService) {
    }

    get selectedCountry(): string {
        return this.addressFormGroup?.controls?.countryISO?.value;
    }

    get showingSearchResult(): boolean {
        return Boolean(this.addressFormGroup.controls.searchRefCode?.value);
    }

    ngOnInit(): void {
        this.address3Helper.resetValidators(this.addressFormGroup);
        this.initValidators();
        this.address3Helper.updateValidity(this.addressFormGroup);

        if(this.showingSearchResult) {
            this.invalidateSearchResultOnFirstEdit();
        }
    }

    private initValidators() {
        // line 1
        this.addressFormGroup.controls.line1.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("line1"))
        ]);

        // line 2
        this.addressFormGroup.controls.line2.setValidators([
            Validators.maxLength(CustomValidators.getAddressMaxlength("line2"))
        ]);

        this.addressFormGroup.controls.city.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("city"))
        ]);

        this.addressFormGroup.controls.postalCode.setValidators([
            Validators.required,
            Validators.pattern(regexValidators.postcodesByCountry.GBR),
            Validators.maxLength(CustomValidators.getAddressMaxlength("postalCode"))
        ]);
    }

    mapSearchResult(place: google.maps.places.PlaceResult) {
        const postalTown = this.address3Helper.getGooglePlaceComponent(place, "postal_town");
        const locality = this.address3Helper.getGooglePlaceComponent(place, "locality");
        const county = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_2");
        const postalCode = this.address3Helper.getGooglePlaceComponent(place, "postal_code");

        this.addressFormGroup.patchValue({
            line1: place.formatted_address.split(",")[0].trim(),
            line2: "",
            city: postalTown || locality,
            province: county,
            postalCode,
            searchRefCode: place.place_id
        });
        this.invalidateSearchResultOnFirstEdit();
    }

    invalidateSearchResultOnFirstEdit() {
        this.logger.debug("Manually edited, invalidating search results");
        this.addressFormGroup.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            first()
        ).subscribe(() => {
            this.addressFormGroup.controls.searchRefCode.setValue("");
        });
    }

}
